import type { Controls } from 'three'
import type { EventBus } from '@/runtime/EventBus'
import type { ControlMode } from '@/shared/types/core'
import type { ControlManager } from '@/shared/types/services'
import type { EngineFrameContext } from '@/shared/types/view'

interface ControlEntry {
  control: Controls<any>
  connected: boolean
}

/** Manages registered Three controls and switches the active one by mode. */
export class ControlManagerImpl implements ControlManager {
  private readonly controls = new Map<ControlMode, ControlEntry>()
  private currentMode: ControlMode = 'none'
  private disposed = false
  private inputActive = true
  /** 绑定的视图 ID；单视口场景为 null */
  readonly viewId: string | null

  constructor(
    private readonly events: EventBus,
    private readonly domElement: HTMLElement,
    options?: { viewId?: string | null; inputActive?: boolean }
  ) {
    this.viewId = options?.viewId ?? null
    this.inputActive = options?.inputActive ?? true
  }

  /** 当前活动控制模式，默认 'none' */
  get activeMode(): ControlMode {
    return this.currentMode
  }

  /** 当前活动控制器；当模式为 none 时返回 null */
  get activeController(): Controls<any> | null {
    if (this.currentMode === 'none') return null
    return this.controls.get(this.currentMode)?.control ?? null
  }

  /**
   * 注册控制器，'none' 为保留模式禁止注册。
   *
   * @param mode - 控制模式标识
   * @param control - 控制器实例
   */
  register(mode: ControlMode, control: Controls<any>): void {
    if (mode === 'none') {
      throw new Error('ControlManager: "none" is reserved and cannot be registered')
    }

    if (this.controls.has(mode)) {
      throw new Error(`ControlManager: controller already registered for mode "${mode}"`)
    }

    if (control.domElement) {
      control.disconnect()
    }
    control.enabled = false

    this.controls.set(mode, {
      control,
      connected: false,
    })
  }

  /**
   * 注销控制器，并在必要时退出当前模式。
   *
   * @param mode - 控制模式
   * @returns 成功移除返回 true
   */
  unregister(mode: ControlMode): boolean {
    if (mode === 'none') {
      return false
    }

    const entry = this.controls.get(mode)
    if (!entry) {
      return false
    }

    if (this.currentMode === mode) {
      this.setMode('none')
    }

    entry.control.enabled = false
    if (entry.connected) {
      entry.control.disconnect()
      entry.connected = false
    }
    entry.control.dispose()
    this.controls.delete(mode)
    return true
  }

  /**
   * 切换控制模式，自动断开旧控制器并连接新控制器。
   *
   * @param mode - 目标模式，传入 'none' 关闭所有控制器
   */
  setMode(mode: ControlMode): void {
    if (mode === this.currentMode) return

    const previous = this.currentMode
    const oldEntry = previous === 'none' ? undefined : this.controls.get(previous)
    const nextEntry = mode === 'none' ? undefined : this.controls.get(mode)

    if (mode !== 'none' && !nextEntry) {
      throw new Error(`ControlManager: no controller registered for mode "${mode}"`)
    }

    if (oldEntry) {
      oldEntry.control.enabled = false
      if (oldEntry.connected) {
        oldEntry.control.disconnect()
        oldEntry.connected = false
      }
    }

    this.currentMode = mode

    if (nextEntry) {
      if (this.inputActive && !nextEntry.connected) {
        nextEntry.control.connect(this.domElement)
        nextEntry.connected = true
      }
      nextEntry.control.enabled = this.inputActive
      if (this.inputActive) {
        nextEntry.control.update(0)
      }
    }

    if (this.viewId) {
      this.events.emit('view.controls.modeChanged', {
        viewId: this.viewId,
        mode,
        previous,
      })
    }
    if (!this.viewId || this.inputActive) {
      this.events.emit('controls.modeChanged', { mode, previous })
    }
  }

  /**
   * 按模式获取控制器。
   *
   * @param mode - 控制模式
   * @returns 控制器实例；未注册返回 undefined
   */
  getController(mode: ControlMode): Controls<any> | undefined {
    return this.controls.get(mode)?.control
  }

  /**
   * 每帧更新活动控制器。
   *
   * @param frame - 引擎帧上下文，提供 delta 时间
   */
  update(frame: EngineFrameContext): void {
    this.activeController?.update(frame.delta)
  }

  /**
   * 切换输入活跃状态，非活跃时断开当前控制器。
   *
   * @param active - 是否允许接收输入
   */
  setInputActive(active: boolean): void {
    if (this.inputActive === active) {
      return
    }

    this.inputActive = active
    const entry = this.currentMode === 'none' ? undefined : this.controls.get(this.currentMode)
    if (!entry) {
      return
    }

    if (active) {
      if (!entry.connected) {
        entry.control.connect(this.domElement)
        entry.connected = true
      }
      entry.control.enabled = true
      entry.control.update(0)
      return
    }

    entry.control.enabled = false
    if (entry.connected) {
      entry.control.disconnect()
      entry.connected = false
    }
  }

  /** 释放管理器，断开并销毁所有控制器 */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true

    const active = this.activeController
    if (active) {
      active.enabled = false
    }
    this.currentMode = 'none'

    for (const entry of this.controls.values()) {
      entry.control.enabled = false
      if (entry.connected) {
        entry.control.disconnect()
        entry.connected = false
      }
      entry.control.dispose()
    }

    this.controls.clear()
  }
}
